<template>
  <div class="recipe-form-container">
    <h1>{{ isEditing ? 'Edit Recipe' : 'Add New Recipe' }}</h1>
    
    <form @submit.prevent="saveRecipe" class="recipe-form">
      <div class="form-group">
        <label for="name">Recipe Name *</label>
        <input 
          type="text" 
          id="name" 
          v-model="form.name" 
          required
          placeholder="e.g., Chocolate Chip Cookies"
        />
      </div>
      
      <div class="form-group">
        <label for="category">Category *</label>
        <input 
          type="text" 
          id="category" 
          v-model="form.category" 
          required
          placeholder="e.g., Desserts, Main Course, Breakfast"
        />
      </div>
      
      <div class="form-group">
        <label for="description">Description</label>
        <textarea 
          id="description" 
          v-model="form.description" 
          rows="2"
          placeholder="A short description of the recipe"
        ></textarea>
      </div>
      
      <div class="form-group">
        <label for="persons">Servings</label>
        <input 
          type="text" 
          id="persons" 
          v-model="form.persons" 
          placeholder="e.g., 4 servings"
        />
      </div>
      
      <div class="form-group">
        <label for="url">Source URL</label>
        <input 
          type="url" 
          id="url" 
          v-model="form.url" 
          placeholder="https://..."
        />
      </div>
      
      <div class="form-group">
        <label for="ingredients">
          Ingredients *
          <span class="help-text">One per line. Use ### for section headers (e.g., ###For the sauce)</span>
        </label>
        <textarea 
          id="ingredients" 
          v-model="ingredientsText" 
          rows="10"
          required
          placeholder="200g flour
100g sugar
2 eggs
###For the frosting
150g butter
200g powdered sugar"
        ></textarea>
      </div>
      
      <div class="form-group">
        <label for="instructions">
          Instructions *
          <span class="help-text">One step per line. Use ### for section headers (e.g., ###Preparation)</span>
        </label>
        <textarea 
          id="instructions" 
          v-model="instructionsText" 
          rows="10"
          required
          placeholder="Preheat oven to 180°C
Mix flour and sugar in a bowl
Add eggs and mix well
###Baking
Pour into a greased pan
Bake for 25 minutes"
        ></textarea>
      </div>
      
      <div class="form-actions">
        <button type="button" class="btn-cancel" @click="$router.back()">Cancel</button>
        <button type="submit" class="btn-save" :disabled="saving">
          {{ saving ? 'Saving...' : (isEditing ? 'Update Recipe' : 'Add Recipe') }}
        </button>
      </div>
      
      <p v-if="error" class="error-message">{{ error }}</p>
    </form>
  </div>
</template>

<script>
export default {
  name: "RecipeForm",
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  
  data() {
    return {
      isEditing: false,
      saving: false,
      error: '',
      form: {
        name: '',
        category: '',
        description: '',
        persons: '',
        url: '',
        ingredients: [],
        instructions: [],
      },
      ingredientsText: '',
      instructionsText: '',
    };
  },
  
  created() {
    // Check if editing existing recipe
    const id = this.$route.params.id;
    if (id && id !== 'new') {
      this.isEditing = true;
      this.loadRecipe(id);
    }
    
    // Redirect if not admin
    if (!this.isAdmin) {
      this.$router.push('/');
    }
  },
  
  methods: {
    async loadRecipe(id) {
      try {
        const response = await this.$http.get("/recipe/findById?id=" + id);
        const recipe = response.data;
        
        this.form = {
          _id: recipe._id,
          name: recipe.name || '',
          category: recipe.category || '',
          description: recipe.description || '',
          persons: recipe.persons || '',
          url: recipe.url || '',
          ingredients: recipe.ingredients || [],
          instructions: recipe.instructions || [],
        };
        
        this.ingredientsText = this.form.ingredients.join('\n');
        this.instructionsText = this.form.instructions.join('\n');
      } catch (e) {
        this.error = 'Failed to load recipe';
        console.error(e);
      }
    },
    
    async saveRecipe() {
      this.saving = true;
      this.error = '';
      
      // Convert text to arrays
      this.form.ingredients = this.ingredientsText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
        
      this.form.instructions = this.instructionsText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      // Get the stored admin key for API authentication
      const apiKey = localStorage.getItem('adminKey');
      const headers = apiKey ? { 'api-key': apiKey } : {};
      
      try {
        if (this.isEditing) {
          await this.$http.put("/recipe/update", this.form, { headers });
        } else {
          await this.$http.post("/recipe/add", this.form, { headers });
        }
        
        this.$router.push('/');
      } catch (e) {
        this.error = 'Failed to save recipe. Please try again.';
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.recipe-form-container {
  max-width: 800px;
  margin: 0 auto;
}

.recipe-form-container h1 {
  margin-bottom: 1.5em;
}

.recipe-form {
  display: flex;
  flex-direction: column;
  gap: 1.5em;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.form-group label {
  font-weight: 500;
  color: var(--text-color);
}

.help-text {
  font-weight: normal;
  font-size: 0.85em;
  color: var(--text-muted, #666);
  display: block;
  margin-top: 0.25em;
}

.form-group input,
.form-group textarea {
  padding: 0.75em 1em;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.5em;
  font-size: 1em;
  background: var(--input-bg, #f2f2f2);
  color: var(--text-color, #333);
  font-family: inherit;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent-color, #0071e3);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-actions {
  display: flex;
  gap: 1em;
  justify-content: flex-end;
  margin-top: 1em;
}

.btn-cancel,
.btn-save {
  padding: 0.75em 1.5em;
  border-radius: 0.5em;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: var(--input-bg, #f2f2f2);
  border: 1px solid var(--border-color, #ddd);
  color: var(--text-color, #333);
}

.btn-cancel:hover {
  background: var(--border-color, #ddd);
}

.btn-save {
  background: var(--accent-color, #0071e3);
  border: none;
  color: white;
}

.btn-save:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #e53935;
  text-align: center;
}
</style>
